/** Unit types and dynamic field definitions for the inventory wizard */

export const WIZARD_MODES = [
  {
    id: 'single',
    icon: '🏠',
    labelAr: 'وحدة واحدة',
    labelEn: 'Single unit',
    descAr: 'فيلا، شقة، محل، أو مكتب منفرد',
    descEn: 'One villa, apartment, shop, or office',
  },
  {
    id: 'bulk',
    icon: '🏘️',
    labelAr: 'عدة وحدات متشابهة',
    labelEn: 'Multiple similar units',
    descAr: 'نفس النوع والمواصفات — ترقيم تلقائي',
    descEn: 'Same type & specs — auto numbering',
  },
  {
    id: 'project',
    icon: '🏗️',
    labelAr: 'مشروع / منتج عقاري',
    labelEn: 'Project / development',
    descAr: 'كمباوند، مجموعة فلل، أو معرض',
    descEn: 'Compound, villa cluster, or showroom block',
  },
];

export const UNIT_TYPES = [
  { value: 'apartment', icon: '🏢', labelAr: 'شقة', labelEn: 'Apartment' },
  { value: 'villa', icon: '🏡', labelAr: 'فيلا', labelEn: 'Villa' },
  { value: 'shop', icon: '🏪', labelAr: 'محل', labelEn: 'Shop' },
  { value: 'showroom', icon: '🏬', labelAr: 'معرض', labelEn: 'Showroom' },
  { value: 'office', icon: '💼', labelAr: 'مكتب', labelEn: 'Office' },
  { value: 'warehouse', icon: '🏭', labelAr: 'مستودع', labelEn: 'Warehouse' },
  { value: 'land', icon: '📐', labelAr: 'أرض', labelEn: 'Land' },
];

/** Unit lifecycle status — inventory truth, not deal pipeline */
export const UNIT_STATUS_OPTIONS = [
  {
    value: 'vacant',
    labelAr: 'متاحة',
    labelEn: 'Available',
    descAr: 'جاهزة للتسويق أو التأجير/البيع',
    descEn: 'Ready to market, rent, or sell',
  },
  {
    value: 'reserved',
    labelAr: 'محجوزة (قيد الإتمام)',
    labelEn: 'Reserved (in progress)',
    descAr: 'عربون أو اتفاقية جارية — لم تُغلق بعد',
    descEn: 'Deposit or agreement in progress — not closed yet',
  },
  {
    value: 'sold',
    labelAr: 'مباعة',
    labelEn: 'Sold',
    descAr: 'تم البيع واكتملت الصفقة',
    descEn: 'Sale completed',
  },
  {
    value: 'rented',
    labelAr: 'مؤجرة',
    labelEn: 'Rented',
    descAr: 'مؤجرة ومستأجرة حالياً',
    descEn: 'Currently leased',
  },
  {
    value: 'maintenance',
    labelAr: 'صيانة',
    labelEn: 'Maintenance',
    descAr: 'غير متاحة مؤقتاً',
    descEn: 'Temporarily unavailable',
  },
];

/** Spec form section order + titles */
export const SPEC_GROUPS = [
  { id: 'size', labelAr: 'المساحات والأرقام', labelEn: 'Size & counts' },
  { id: 'rooms', labelAr: 'الغرف', labelEn: 'Rooms' },
  { id: 'outdoor', labelAr: 'الخارج والمرافق', labelEn: 'Outdoor & amenities' },
  { id: 'extras', labelAr: 'إضافات', labelEn: 'Extras' },
];

const ROOMS = [
  { key: 'has_majlis', type: 'boolean', group: 'rooms', labelAr: 'مجلس', labelEn: 'Majlis' },
  { key: 'has_living_room', type: 'boolean', group: 'rooms', labelAr: 'صالة معيشة', labelEn: 'Living room' },
  { key: 'has_kitchen', type: 'boolean', group: 'rooms', labelAr: 'مطبخ', labelEn: 'Kitchen' },
  { key: 'has_maid_room', type: 'boolean', group: 'rooms', labelAr: 'غرفة خادمة', labelEn: 'Maid room' },
];

export const UNIT_TYPE_FIELDS = {
  apartment: [
    { key: 'bedrooms', column: 'bedrooms', type: 'number', group: 'size', labelAr: 'غرف النوم', labelEn: 'Bedrooms', min: 0 },
    { key: 'bathrooms', column: 'bathrooms', type: 'number', group: 'size', labelAr: 'دورات المياه', labelEn: 'Bathrooms', min: 0 },
    { key: 'area_sqm', column: 'area_sqm', type: 'number', group: 'size', labelAr: 'المساحة (م²)', labelEn: 'Area (m²)', min: 0, step: 0.1 },
    { key: 'floor_number', column: 'floor_number', type: 'number', group: 'size', labelAr: 'الطابق', labelEn: 'Floor', min: 0 },
    ...ROOMS,
    { key: 'has_balcony', type: 'boolean', group: 'extras', labelAr: 'شرفة', labelEn: 'Balcony' },
    { key: 'has_parking', type: 'boolean', group: 'extras', labelAr: 'موقف سيارة', labelEn: 'Parking' },
  ],
  villa: [
    { key: 'bedrooms', column: 'bedrooms', type: 'number', group: 'size', labelAr: 'غرف النوم', labelEn: 'Bedrooms', min: 0 },
    { key: 'bathrooms', column: 'bathrooms', type: 'number', group: 'size', labelAr: 'دورات المياه', labelEn: 'Bathrooms', min: 0 },
    { key: 'area_sqm', column: 'area_sqm', type: 'number', group: 'size', labelAr: 'مساحة البناء (م²)', labelEn: 'Built-up area (m²)', min: 0, step: 0.1 },
    { key: 'land_area_sqm', type: 'number', group: 'size', labelAr: 'مساحة الأرض (م²)', labelEn: 'Land area (m²)', min: 0 },
    { key: 'floor_number', column: 'floor_number', type: 'number', group: 'size', labelAr: 'عدد الأدوار', labelEn: 'Floors', min: 0 },
    ...ROOMS,
    // One outdoor space concept (حوش) — not also "حديقة"
    { key: 'has_private_yard', type: 'boolean', group: 'outdoor', labelAr: 'حوش / حديقة', labelEn: 'Yard / garden' },
    {
      key: 'yard_area_sqm',
      type: 'number',
      group: 'outdoor',
      labelAr: 'مساحة الحوش (م²)',
      labelEn: 'Yard area (m²)',
      min: 0,
      showIf: { key: 'has_private_yard', truthy: true },
    },
    { key: 'has_pool', type: 'boolean', group: 'outdoor', labelAr: 'مسبح', labelEn: 'Pool' },
    { key: 'has_independent_car_entrance', type: 'boolean', group: 'outdoor', labelAr: 'مدخل سيارة مستقل', labelEn: 'Independent car entrance' },
  ],
  shop: [
    { key: 'area_sqm', column: 'area_sqm', type: 'number', group: 'size', labelAr: 'المساحة (م²)', labelEn: 'Area (m²)', min: 0 },
    { key: 'frontage_m', type: 'number', group: 'size', labelAr: 'واجهة (م)', labelEn: 'Frontage (m)', min: 0 },
    { key: 'floor_number', column: 'floor_number', type: 'number', group: 'size', labelAr: 'الطابق', labelEn: 'Floor', min: 0 },
    { key: 'activity_type', type: 'text', group: 'extras', labelAr: 'نوع النشاط', labelEn: 'Activity type' },
  ],
  showroom: [
    { key: 'area_sqm', column: 'area_sqm', type: 'number', group: 'size', labelAr: 'المساحة (م²)', labelEn: 'Area (m²)', min: 0 },
    { key: 'frontage_m', type: 'number', group: 'size', labelAr: 'واجهة (م)', labelEn: 'Frontage (m)', min: 0 },
    { key: 'ceiling_height_m', type: 'number', group: 'size', labelAr: 'ارتفاع السقف (م)', labelEn: 'Ceiling height (m)', min: 0, step: 0.1 },
    { key: 'floor_number', column: 'floor_number', type: 'number', group: 'size', labelAr: 'الطابق', labelEn: 'Floor', min: 0 },
  ],
  office: [
    { key: 'area_sqm', column: 'area_sqm', type: 'number', group: 'size', labelAr: 'المساحة (م²)', labelEn: 'Area (m²)', min: 0 },
    { key: 'floor_number', column: 'floor_number', type: 'number', group: 'size', labelAr: 'الطابق', labelEn: 'Floor', min: 0 },
    { key: 'furnished', type: 'boolean', group: 'extras', labelAr: 'مفروش', labelEn: 'Furnished' },
    { key: 'parking_spots', type: 'number', group: 'extras', labelAr: 'مواقف', labelEn: 'Parking spots', min: 0 },
  ],
  warehouse: [
    { key: 'area_sqm', column: 'area_sqm', type: 'number', group: 'size', labelAr: 'المساحة (م²)', labelEn: 'Area (m²)', min: 0 },
    { key: 'ceiling_height_m', type: 'number', group: 'size', labelAr: 'ارتفاع السقف (م)', labelEn: 'Ceiling height (m)', min: 0, step: 0.1 },
    { key: 'loading_dock', type: 'boolean', group: 'extras', labelAr: 'رصيف تحميل', labelEn: 'Loading dock' },
  ],
  land: [
    { key: 'area_sqm', column: 'area_sqm', type: 'number', group: 'size', labelAr: 'المساحة (م²)', labelEn: 'Area (m²)', min: 0 },
    { key: 'street_width_m', type: 'number', group: 'size', labelAr: 'عرض الشارع (م)', labelEn: 'Street width (m)', min: 0 },
    { key: 'facades', type: 'number', group: 'size', labelAr: 'عدد الواجهات', labelEn: 'Facades', min: 1, max: 4 },
    { key: 'zoning', type: 'text', group: 'extras', labelAr: 'التصنيف', labelEn: 'Zoning' },
  ],
};

export const SAUDI_CITIES = [
  'الرياض', 'جدة', 'الدمام', 'مكة', 'المدينة', 'الخبر', 'الظهران', 'تبوك', 'بريدة',
  'خميس مشيط', 'أبها', 'الطائف', 'حائل', 'نجران', 'جازان', 'ينبع', 'الجبيل', 'الأحساء',
];

export function getUnitTypeLabel(type, language) {
  const t = UNIT_TYPES.find((x) => x.value === type);
  if (!t) return type;
  return language === 'ar' ? t.labelAr : t.labelEn;
}

export function getStatusLabel(status, language) {
  const s = UNIT_STATUS_OPTIONS.find((x) => x.value === status);
  if (!s) return status;
  return language === 'ar' ? s.labelAr : s.labelEn;
}

export function getFieldsForType(unitType) {
  return UNIT_TYPE_FIELDS[unitType] || UNIT_TYPE_FIELDS.apartment;
}

export function fieldIsVisible(field, specs = {}) {
  if (!field?.showIf) return true;
  const raw = specs[field.showIf.key];
  if (field.showIf.truthy) return Boolean(raw);
  return raw === field.showIf.equals;
}

/** Group fields for the specs step UI (preserves SPEC_GROUPS order). */
export function getGroupedFieldsForType(unitType, specs = {}) {
  const fields = getFieldsForType(unitType).filter((f) => fieldIsVisible(f, specs));
  return SPEC_GROUPS
    .map((g) => ({
      ...g,
      fields: fields.filter((f) => (f.group || 'size') === g.id),
    }))
    .filter((g) => g.fields.length > 0);
}

export function generateUnitNumbers({ count, prefix = '', start = 1, pad = 0 }) {
  const n = Math.min(Math.max(parseInt(count, 10) || 1, 1), 500);
  return Array.from({ length: n }, (_, i) => {
    const num = start + i;
    const core = pad > 0 ? String(num).padStart(pad, '0') : String(num);
    return `${prefix}${core}`;
  });
}

/**
 * Assign per-unit statuses for bulk/project: sold → rented → reserved → vacant remainder.
 */
export function assignBulkStatuses(count, { sold = 0, rented = 0, reserved = 0 } = {}) {
  const n = Math.max(0, parseInt(count, 10) || 0);
  const s = Math.max(0, parseInt(sold, 10) || 0);
  const r = Math.max(0, parseInt(rented, 10) || 0);
  const res = Math.max(0, parseInt(reserved, 10) || 0);
  const statuses = [];
  for (let i = 0; i < n; i += 1) {
    if (i < s) statuses.push('sold');
    else if (i < s + r) statuses.push('rented');
    else if (i < s + r + res) statuses.push('reserved');
    else statuses.push('vacant');
  }
  return statuses;
}

export function buildUnitPayload(form, unitNumber, statusOverride) {
  const fields = getFieldsForType(form.unitType);
  const metadata = {
    available_for: form.availableFor || 'rent',
  };
  if (form.salePrice) metadata.sale_price = parseFloat(form.salePrice);

  const payload = {
    unit_number: unitNumber,
    unit_type: form.unitType,
    status: statusOverride || form.status || 'vacant',
    monthly_rent: form.monthlyRent ? parseFloat(form.monthlyRent) : null,
    notes: form.notes || null,
    metadata,
  };

  fields.forEach((field) => {
    if (!fieldIsVisible(field, form.specs)) return;
    const raw = form.specs?.[field.key];
    if (raw === '' || raw == null) return;
    if (field.type === 'boolean') {
      const val = Boolean(raw);
      if (field.column) payload[field.column] = val ? 1 : 0;
      else metadata[field.key] = val;
      return;
    }
    const val = field.type === 'number' ? parseFloat(raw) : raw;
    if (Number.isNaN(val)) return;
    if (field.column) payload[field.column] = val;
    else metadata[field.key] = val;
  });

  // Legacy: map yard to garden flag for older readers
  if (metadata.has_private_yard) metadata.has_garden = true;

  return payload;
}

export function defaultSpecsForType(unitType) {
  const specs = {};
  getFieldsForType(unitType).forEach((f) => {
    if (f.type === 'boolean') specs[f.key] = false;
    else specs[f.key] = '';
  });
  return specs;
}
